import React from 'react';
import styled from 'styled-components';
import { SendIcon, Loader2Icon } from 'lucide-react';
import { useFluidForm } from './FormContext';
import { useStyleConfig } from './EmbeddedStyles';
import { formatInferredValue } from '../../../shared';
import { groupBy } from 'ramda';
const Card = styled.div `
  border-radius: 0.75rem;
  overflow: hidden;
  font-family: inherit;
  border: 1px solid ${props => props.$styles.chatAssistantBubbleBorderHex};
  background: linear-gradient(
    to bottom right,
    ${props => props.$styles.chatAssistantBubbleColor},
    ${props => props.$styles.chatAssistantBubbleGradientEndHex}
  );
  color: ${props => props.$styles.chatAssistantBubbleFontColor};
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
`;
const CardMessage = styled.div `
  padding: 0.875rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-family: inherit;
  color: inherit;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;
const SectionHeader = styled.div `
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  font-family: inherit;
  color: inherit;
  opacity: 0.7;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;
const FieldRow = styled.div `
  padding: 0.375rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
`;
const FieldLabel = styled.span `
  flex-shrink: 0;
  font-size: 0.9rem;
  font-family: inherit;
  color: inherit;
  opacity: 0.65;
`;
const FieldValue = styled.span `
  font-weight: 500;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-word;
  font-family: inherit;
  color: inherit;
`;
const ButtonRow = styled.div `
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;
export const ChatSummaryCard = ({ message, onSubmit, isSubmitting = false, }) => {
    const form = useFluidForm();
    const styles = useStyleConfig();
    const allFields = form.getRenderViewMap();
    const inputFields = Object.values(allFields).filter(field => form.isInputField(field));
    const fieldsBySection = groupBy((field) => form.getFieldSection(field.fieldPath)?.label || 'General', inputFields);
    return (React.createElement(Card, { "$styles": styles },
        message && (React.createElement(CardMessage, { dangerouslySetInnerHTML: { __html: message } })),
        Object.entries(fieldsBySection).map(([sectionTitle, fields]) => (React.createElement("div", { key: sectionTitle },
            Object.keys(fieldsBySection).length > 1 && (React.createElement(SectionHeader, null, sectionTitle)),
            fields.map((field) => {
                const config = form.getConfig(field.fieldPath);
                const value = form.getValue(field.fieldPath);
                const displayValue = formatInferredValue(value, config) || '--';
                return (React.createElement(FieldRow, { key: field.fieldPath },
                    React.createElement(FieldLabel, null, config?.label || field.fieldPath),
                    React.createElement(FieldValue, null, typeof displayValue === 'object' ? JSON.stringify(displayValue) : String(displayValue))));
            })))),
        React.createElement(ButtonRow, null,
            React.createElement("button", { className: "fluid-button-primary", onClick: onSubmit, disabled: isSubmitting, style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem' } },
                isSubmitting ? (React.createElement(Loader2Icon, { size: 14, className: "animate-spin" })) : (React.createElement(SendIcon, { size: 14 })),
                "Submit"))));
};
